import { StateCreator } from 'zustand';
import {
  getPatients,
  createPatient,
  updatePatient,
  deletePatient,
  client,
} from '../../services/api';
import type {
  PatientResponse as ApiPatientResponse,
  PatientCreate,
  PatientUpdate,
} from '../../services/api/types.gen';

export interface Patient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  status: 'Active' | 'Pending' | 'Inactive';
  lastVisit: string;
  nextVisit: string;
  diagnosis: string;
  mrn: string;
  risk: 'High' | 'Medium' | 'Low';
  // Backend fields
  first_name: string;
  last_name: string;
  date_of_birth: string;
  ssn?: string;
  emergency_contact?: string;
  emergency_phone?: string;
  needs_legal_representative: boolean;
  has_poa: boolean;
  insurance_name?: string;
  clinic_name?: string;
  created_at: string;
  updated_at: string;
}

export interface PatientsSlice {
  patients: Patient[];
  selectedPatient: Patient | null;
  isPatientsLoading: boolean;
  error: string | null;
  searchTerm: string;
  totalPatients: number;
  showInactive: boolean;
  setPatients: (patients: Patient[]) => void;
  addPatient: (patient: Omit<Patient, 'id'>) => void;
  updatePatient: (id: string, updates: Partial<Patient>) => void;
  deletePatient: (id: string) => void;
  setSelectedPatient: (patient: Patient | null) => void;
  setPatientsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSearchTerm: (term: string) => void;
  setTotalPatients: (total: number) => void;
  setShowInactive: (showInactive: boolean) => void;
  getFilteredPatients: () => Patient[];
  fetchPatients: () => Promise<void>;
  createPatient: (patientData: PatientCreate) => Promise<void>;
  updatePatientApi: (id: string, updates: PatientUpdate) => Promise<void>;
  deletePatientApi: (id: string) => Promise<void>;
}

// Helper function to convert API response to frontend Patient format
const convertApiPatientToPatient = (
  apiPatient: ApiPatientResponse
): Patient => {
  if (!apiPatient) {
    console.error('Received null/undefined patient data');
    throw new Error('Invalid patient data received from API');
  }

  return {
    id: apiPatient.id || '',
    name: `${apiPatient.first_name || ''} ${apiPatient.last_name || ''}`.trim(),
    email: apiPatient.email || undefined,
    phone: apiPatient.phone || undefined,
    address: apiPatient.address || undefined,
    city: apiPatient.city || undefined,
    state: apiPatient.state || undefined,
    zip_code: apiPatient.zip_code || undefined,
    status: 'Active',
    lastVisit: '2024-01-15',
    nextVisit: '2024-01-22',
    diagnosis: 'General',
    mrn: apiPatient.mrn || 'N/A',
    risk: 'Low',
    first_name: apiPatient.first_name || '',
    last_name: apiPatient.last_name || '',
    date_of_birth: apiPatient.date_of_birth || '',
    ssn: apiPatient.ssn || undefined,
    emergency_contact: apiPatient.emergency_contact || undefined,
    emergency_phone: apiPatient.emergency_phone || undefined,
    needs_legal_representative: apiPatient.needs_legal_representative || false,
    has_poa: apiPatient.has_poa || false,
    insurance_name: apiPatient.insurance_name || undefined,
    clinic_name: apiPatient.clinic_name || undefined,
    created_at: apiPatient.created_at || '',
    updated_at: apiPatient.updated_at || '',
  };
};

export const patientsSlice: StateCreator<PatientsSlice> = (set, get) => ({
  patients: [],
  selectedPatient: null,
  isPatientsLoading: false,
  error: null,
  searchTerm: '',
  totalPatients: 0,
  showInactive: false,

  setPatients: (patients: Patient[]) => {
    set({ patients });
  },

  addPatient: (patient: Omit<Patient, 'id'>) => {
    const newPatient: Patient = {
      ...patient,
      id: Math.random().toString(36).substr(2, 9), // Temporary ID generation
    };
    set(state => ({ patients: [...state.patients, newPatient] }));
  },

  updatePatient: (id: string, updates: Partial<Patient>) => {
    set(state => ({
      patients: state.patients.map(patient =>
        patient.id === id ? { ...patient, ...updates } : patient
      ),
    }));
  },

  deletePatient: (id: string) => {
    set(state => ({
      patients: state.patients.filter(patient => patient.id !== id),
    }));
  },

  setSelectedPatient: (patient: Patient | null) => {
    set({ selectedPatient: patient });
  },

  setPatientsLoading: (loading: boolean) => {
    set({ isPatientsLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  setSearchTerm: (term: string) => {
    set({ searchTerm: term });
  },

  setTotalPatients: (total: number) => {
    set({ totalPatients: total });
  },

  setShowInactive: (showInactive: boolean) => {
    set({ showInactive });
  },

  getFilteredPatients: () => {
    const { patients, searchTerm } = get();
    return patients.filter(
      patient =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patient.email &&
          patient.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (patient.phone && patient.phone.includes(searchTerm))
    );
  },

  fetchPatients: async () => {
    const {
      setPatientsLoading,
      setPatients,
      setTotalPatients,
      setError,
      showInactive,
    } = get();
    try {
      setPatientsLoading(true);
      setError(null);
      const response = await getPatients({
        client,
        query: { show_inactive: showInactive },
      });
      if (response && response.data) {
        const patients = response.data.map(convertApiPatientToPatient);
        set({
          patients,
          totalPatients: patients.length,
          isPatientsLoading: false,
        });
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError('Failed to load patients. Please try again.');
      // Set empty array on error to prevent undefined issues
      setPatients([]);
      setTotalPatients(0);
    } finally {
      setPatientsLoading(false);
    }
  },

  createPatient: async (patientData: PatientCreate) => {
    const { fetchPatients } = get();
    try {
      await createPatient({
        body: patientData,
        client,
      });
      await fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  },

  updatePatientApi: async (id: string, updates: PatientUpdate) => {
    const { fetchPatients } = get();
    try {
      await updatePatient({
        path: { patient_id: id },
        body: updates,
        client,
      });
      await fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  },

  deletePatientApi: async (id: string) => {
    const { fetchPatients } = get();
    try {
      await deletePatient({
        path: { patient_id: id },
        client,
      });
      await fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error deleting patient:', error);
      throw error;
    }
  },
});
